/**
 * The notification channels (phase 7b item 4): each channel's kind, where it
 * goes (a hint — never a URL or secret), its lowest severity and sites, on or
 * off; add, test (one, or every enabled channel) and delete. The last test's
 * results are shown per channel.
 */
import { CircleHelpIcon, MailIcon, PlusIcon, SendIcon, Trash2Icon, WebhookIcon } from 'lucide-react';
import type { AlertChannelWire, TestAlertResultWire } from '@norna/api-contract/alerts.js';
import { severityTone } from '../../lib/alerts.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type AlertChannelsProps = {
  readonly channels: readonly AlertChannelWire[];
  /** The last test send's results, by channel; empty before one. */
  readonly testResults: readonly TestAlertResultWire[];
  readonly testing: boolean;
  /** The channel being changed or deleted right now, if any. */
  readonly busyChannelId: string | null;
  readonly error: string | null;
  readonly onAdd: () => void;
  readonly onTest: (channelId: string | null) => void;
  readonly onToggle: (channelId: string, enabled: boolean) => void;
  readonly onDelete: (channelId: string) => void;
};

function destination(c: AlertChannelWire): string {
  if (c.kind !== 'email') return c.target ?? '—';
  const groups = c.recipients?.groupIds ?? [];
  const users = c.recipients?.userIds ?? [];
  return [groups.length === 0 ? null : `groups ${groups.join(', ')}`, users.length === 0 ? null : `${String(users.length)} user(s)`].filter((p) => p !== null).join(' · ');
}

function KindIcon({ kind }: { readonly kind: AlertChannelWire['kind'] }) {
  // A kind from a newer cloud (`unknown`, D37) gets no kind's icon.
  if (kind === 'unknown') return <CircleHelpIcon className="size-4" aria-hidden="true" />;
  return kind === 'email' ? <MailIcon className="size-4" aria-hidden="true" /> : <WebhookIcon className="size-4" aria-hidden="true" />;
}

export function AlertChannels({ channels, testResults, testing, busyChannelId, error, onAdd, onTest, onToggle, onDelete }: AlertChannelsProps) {
  return (
    <Card data-testid="alert-channels">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Channels</CardTitle>
          <CardDescription>Where alerts are sent: email to users and groups, a Slack channel, or your own webhook (signed).</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onTest(null)} disabled={testing || !channels.some((c) => c.enabled)} data-testid="test-all">
            <SendIcon />
            {testing ? 'Sending…' : 'Send a test'}
          </Button>
          <Button onClick={onAdd} data-testid="add-channel">
            <PlusIcon />
            Add channel
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error !== null ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {channels.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="channels-empty">
            No channels yet: alerts are recorded, but nobody is told. Add one.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">On</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Goes to</TableHead>
                <TableHead className="w-28">From</TableHead>
                <TableHead>Sites</TableHead>
                <TableHead className="w-48">Last test</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {channels.map((c) => {
                const result = testResults.find((r) => r.channelId === c.channelId);
                const busy = busyChannelId === c.channelId;
                return (
                  <TableRow key={c.channelId} data-testid="channel-row" data-channel-id={c.channelId}>
                    <TableCell>
                      <input type="checkbox" className="size-4 accent-primary" aria-label={`${c.name} on`} checked={c.enabled} disabled={busy} onChange={(event) => onToggle(c.channelId, event.target.checked)} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium">
                        <KindIcon kind={c.kind} />
                        {c.name}
                      </div>
                      <div className="text-xs text-muted-foreground">{c.kind}</div>
                    </TableCell>
                    <TableCell className="text-sm">{destination(c)}</TableCell>
                    <TableCell>
                      <Badge variant={severityTone(c.minSeverity)}>{c.minSeverity}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{c.siteIds === null ? 'every site' : c.siteIds.join(', ')}</TableCell>
                    <TableCell className="text-sm" data-testid="channel-test-result">
                      {result === undefined ? '—' : result.ok ? <Badge variant="success">delivered</Badge> : <span className="text-destructive">{result.detail}</span>}
                    </TableCell>
                    <TableCell className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" aria-label={`Test ${c.name}`} onClick={() => onTest(c.channelId)} disabled={testing || busy}>
                        <SendIcon />
                      </Button>
                      <Button variant="ghost" size="icon" aria-label={`Delete ${c.name}`} onClick={() => onDelete(c.channelId)} disabled={busy}>
                        <Trash2Icon />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
